import React, { useEffect, useState } from 'react'
import { Row, Col, Radio, Space, Checkbox, Popover, Typography, Divider, Button, Tooltip, Switch } from 'antd'
import { CreateReq, MultiUom } from 'Store/product/types'
import { AppInput, FormItem, LabelItem } from 'Components/form'
import styled from 'styled-components'
import { Data } from 'Store/delivery-cost/types'
import { DeleteOutlined, EditOutlined, InfoCircleOutlined, InfoCircleTwoTone } from '@ant-design/icons'
import { TableListPage } from 'Components/common/Table'
import { Theme } from 'Components/common'
import theme, { MyTheme } from 'Components/common/Theme'
import { ActionButton } from 'Components/button'
import { DefaultStatus } from 'helpers/constanst'
import LinkText from 'Components/text/LinkText'
import { decimalCount, formValidation, isASCII, isFormError, isFormRequired } from 'helpers/format'
import { cloneDeep } from 'lodash'
const { Text } = Typography

type Props = {
  formData: MultiUom
  onUpdate: (formData: MultiUom) => void
  onDelete: () => void
  getConfigName: (name: string) => string
  index: number
  mainUom: string
}

const MultiUomPage: React.FunctionComponent<Props> = props => {
  const { formData, onUpdate, getConfigName, index = 0, onDelete, mainUom } = props
  const [formError, setFormError] = useState([] as string[])
  const multiUomsrequireFeild = ['uom_name', 'price', 'multiplier', 'offline_price']

  useEffect(() => {
    if (formData) {
      validateMultiUomsForm()
    }
    return () => {
      setFormError([])
    }
  }, [formData])

  const validateMultiUomsForm = () => {
    const error = formValidation(multiUomsrequireFeild, formData)
    if (formData.barcode !== '') {
      const isAscii = isASCII(formData.barcode)
      if (!isAscii) {
        error.push('barcode_format')
      }
    }

    if (!formData.multiplier || formData.multiplier < 2) {
      error.push('multiplier')
    }

    setFormError([...error])
    return error.length > 0 ? false : true
  }

  const isError = (name: string) => {
    return isFormError(formError, name)
  }

  const isRequired = (name: string) => {
    return isFormRequired(multiUomsrequireFeild, name)
  }

  const hideLabel = index !== 0
  const canDelete = formData?.id === 0

  const onUpdateData = (val: any, key: string) => {
    let newVal = cloneDeep(val)
    if (key === 'multiplier') {
      newVal = parseInt(val)
    }
    if (key === 'status') {
      newVal = val ? DefaultStatus.ACTIVE : DefaultStatus.INACTIVE
    }
    if (
      key === 'price' ||
      key === 'offline_price' ||
      key === 'price_a' ||
      key === 'price_b' ||
      key === 'price_c'
    ) {
      const count = decimalCount(val)
      if (count > 2) return
    }
    onUpdate({
      ...formData,
      [key]: newVal,
    })
    validateMultiUomsForm()
  }

  const uomTitle = `${formData.uom_name}`
  const remark = `1 ${formData.uom_name || 'หน่วย'} = ${formData.multiplier || 0} ${mainUom}`
  const disableMultiplier = formData?.id && formData?.id > 0

  return (
    <Row gutter={[8, 6]} justify="start" align="middle">
      <Col span={24}>
        <Row justify="space-between" align="middle">
          <Col>
            <NameUom>
              {formData.uom_name !== '' && (
                <>
                  {uomTitle} <Title>({remark})</Title> &nbsp;&nbsp;&nbsp;&nbsp;
                </>
              )}
              {canDelete && (
                <Link onClick={onDelete}>
                  <DeleteOutlined /> ลบหน่วยสินค้า
                </Link>
              )}
            </NameUom>
          </Col>
          <Col>
            <SwitchStyled>
              <Switch
                checked={formData.status === DefaultStatus.ACTIVE}
                onChange={(val: boolean) => onUpdateData(val, 'status')}
              />
              &nbsp; &nbsp;
              <LabelItem
                label={`สถานะ : ${formData.status === DefaultStatus.ACTIVE ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}`}
              />{' '}
            </SwitchStyled>
          </Col>
        </Row>
      </Col>
      {formData.status === DefaultStatus.ACTIVE && (
        <>
          <Col md={8} xs={12}>
            <AppInput
              name="uom_name"
              label={'ชื่อหน่วยสินค้า'}
              isRequired={isRequired('uom_name')}
              isError={isError('uom_name')}
              value={formData.uom_name}
              onChange={onUpdateData}
              placeHolder="ระบุชื่อหน่วยสินค้าอื่นๆ"
            />
          </Col>
          {/* <Col md={1} xs={11}>
            <Center>=</Center>
          </Col> */}
          <Col md={8} xs={12}>
            <AppInput
              name="multiplier"
              label={`${mainUom} (หน่วยสินค้าหลัก)`}
              type="number"
              isRequired={isRequired('multiplier')}
              errorMsg={isError('multiplier') ? 'จำนวนสำหรับการขายและซื้อมากกว่า 1 หน่วย' : ''}
              isError={isError('multiplier')}
              value={formData.multiplier}
              onChange={onUpdateData}
              resProps={{
                disabled: disableMultiplier,
                prefix: '=',
                allowClear: false,
                suffix: mainUom,
              }}
              labelIcon={
                <Tooltip title="คือ ค่าของหน่วยนั้นๆ ที่เทียบเท่ากับหน่วยหลัก (อัตราส่วนในการแปลงหน่วย หรือ Conversion ratio)">
                  &nbsp; <InfoCircleOutlined />
                </Tooltip>
              }
            />
          </Col>
          <Col md={8} xs={12}>
            <AppInput
              name="barcode"
              label="​Barcode"
              isRequired={isRequired('barcode')}
              specialRequired={formData.barcode !== ''}
              isError={isError('barcode') || isError('barcode_format')}
              errorMsg={isError('barcode_format') ? 'รูปแบบ Barcode ให้ถูกต้อง' : 'กรุณาระบุ Barcode'}
              value={formData.barcode}
              onChange={onUpdateData}
              labelIcon={
                <Tooltip title="Barcode รองรับชุดตัวอักษร 128 ASCII (ไม่รองรับภาษาไทย)">
                  &nbsp; <InfoCircleOutlined />
                </Tooltip>
              }
            />
          </Col>
          <Col md={8} xs={12}>
            <AppInput
              name="offline_price"
              label={'ราคาขายหน้าร้าน'}
              type="number"
              isRequired={isRequired('offline_price')}
              isError={isError('offline_price')}
              value={formData.offline_price}
              onChange={onUpdateData}
              //resProps={{ suffix: 'บาท' }}
            />
          </Col>
          <Col md={8} xs={12}>
            <AppInput
              name="purchase_price"
              label={'ราคาซื้อ'}
              type="number"
              isRequired={isRequired('purchase_price')}
              isError={isError('purchase_price')}
              value={formData.purchase_price}
              onChange={onUpdateData}
              labelIcon={
                <Tooltip title="ราคาซื้อ คือข้อมูลที่บันทึกไว้สำหรับเป็นค่า default (ค่าเริ่มต้น) ของต้นทุนของสินค้าในการออกใบจัดซื้อ">
                  &nbsp; <InfoCircleOutlined />
                </Tooltip>
              }
              //resProps={{ suffix: 'บาท' }}
            />
          </Col>
          <Col md={24} xs={24} className="ttr-productoption-price-special">
            <Row justify="space-between" align="middle">
              <Col>
                <Title>ราคาอื่นๆเพิ่มเติม (ใช้สำหรับระบบขายราคาพิเศษ)</Title>
              </Col>
            </Row>
            <Row gutter={[8, 8]}>
              <Col md={8} xs={12}>
                <AppInput
                  name="price_a"
                  label={getConfigName('price_a')}
                  isRequired={isRequired('price_a')}
                  isError={isError('price_a')}
                  value={formData.price_a}
                  onChange={onUpdateData}
                  type="number"
                  //resProps={{ suffix: 'บาท' }}
                />
              </Col>
              <Col md={8} xs={12}>
                <AppInput
                  name="price_b"
                  label={getConfigName('price_b')}
                  isRequired={isRequired('price_b')}
                  isError={isError('price_b')}
                  value={formData.price_b}
                  onChange={onUpdateData}
                  type="number"
                  //resProps={{ suffix: 'บาท' }}
                />
              </Col>
              <Col md={8} xs={12}>
                <AppInput
                  name="price_c"
                  label={getConfigName('price_c')}
                  isRequired={isRequired('price_c')}
                  isError={isError('price_c')}
                  value={formData.price_c}
                  onChange={onUpdateData}
                  type="number"
                  //resProps={{ suffix: 'บาท' }}
                />
              </Col>
            </Row>
          </Col>
        </>
      )}
      <Divider style={{ margin: '1px 0' }} />
    </Row>
  )
}

const HeaderForm = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
  //font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
`

const Title = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  margin-bottom: 10px;
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
`

const NameUom = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body2};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
  margin-bottom: 10px;
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
`

const SwitchStyled = styled.div`
  margin: 10px 0;
`

const Link: any = styled(LinkText)`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.error};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

export default MultiUomPage
